import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppNav, { NAV_SAFE_CLASS } from "@/components/nav/AppNav";
import { UserPlus, Check, X, Loader2, Users, Mail, RefreshCw } from "lucide-react";
import { useChatSocket } from "@/hooks/useChatSocket";
import { useAuth } from "@/context/AuthContext";

function Avatar({ src, name, size = 48 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full bg-[#CCFF00] border-hard-2 flex items-center justify-center overflow-hidden font-display shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.2 }}
    >
      {src ? <img src={src} alt={name} className="w-full h-full object-cover" /> : <span>{initial}</span>}
    </div>
  );
}

export default function FriendsPage() {
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { user } = useAuth() || {};

  const load = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([
        api.get("/friends"),
        api.get("/friends/requests"),
      ]);
      setFriends(f.data);
      setIncoming(r.data.incoming);
      setOutgoing(r.data.outgoing);
    } catch (e) { setError(formatApiError(e)); }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when this tab regains focus — catches requests received while
  // the tab was in the background.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  // Live WS: when a friend request arrives / is accepted, reload the lists
  // so this page reflects the latest state without a manual refresh.
  const handleWsEvent = useCallback((data) => {
    if (!data || !data.type) return;
    if (data.type === "friend_request" || data.type === "friend_accepted" || data.type === "friend_declined") {
      load();
    }
  }, [load]);
  useChatSocket({ enabled: !!user, onMessage: handleWsEvent });

  const manualRefresh = () => {
    setRefreshing(true);
    load();
  };

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError("");
    setOk("");
    try {
      const { data } = await api.post("/friends/request", { email: email.trim() });
      setEmail("");
      if (data.status === "accepted") setOk("Otomatik eşleşme! Artık arkadaşsınız 🎉");
      else setOk("İstek gönderildi 📬");
      await load();
      window.dispatchEvent(new Event("yappo-friends-changed"));
    } catch (err) { setError(formatApiError(err)); }
    setSending(false);
    setTimeout(() => setOk(""), 3500);
  };

  const accept = async (id) => {
    try {
      await api.post(`/friends/requests/${id}/accept`);
      await load();
      window.dispatchEvent(new Event("yappo-friends-changed"));
    } catch (e) { alert(formatApiError(e)); }
  };
  const decline = async (id) => {
    try {
      await api.post(`/friends/requests/${id}/decline`);
      await load();
      window.dispatchEvent(new Event("yappo-friends-changed"));
    } catch (e) { alert(formatApiError(e)); }
  };
  const cancel = async (id) => {
    try {
      await api.delete(`/friends/requests/${id}`);
      await load();
      window.dispatchEvent(new Event("yappo-friends-changed"));
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="friends-page">
      <AppNav />
      <div className={NAV_SAFE_CLASS}>
        <div className="max-w-2xl mx-auto p-4 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <h1 className="font-display text-3xl md:text-4xl">Arkadaşlar</h1>
            <button
              onClick={manualRefresh}
              data-testid="friends-refresh-btn"
              disabled={refreshing}
              className="ml-auto relative w-10 h-10 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm disabled:opacity-60"
              aria-label="Yenile"
              title="Yenile"
            >
              <RefreshCw strokeWidth={3} className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {incoming.length > 0 && (
                <span
                  data-testid="friends-refresh-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black flex items-center justify-center border-[2px] border-white"
                >
                  {incoming.length > 9 ? "9+" : incoming.length}
                </span>
              )}
            </button>
          </div>

          {/* Add friend by email */}
          <form onSubmit={send} className="bg-white border-hard shadow-hard rounded-2xl p-4 mb-6" data-testid="add-friend-form">
            <div className="text-[11px] uppercase font-black tracking-widest mb-2 flex items-center gap-1"><Mail strokeWidth={3} className="w-3 h-3" /> E-posta ile arkadaş ekle</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="friend-email-input"
                placeholder="ornek@yappo.app"
                className="flex-1 bg-[#F9F8F6] border-hard rounded-xl px-4 py-2.5 focus:outline-none focus:bg-white"
                required
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="send-friend-req-btn"
                className="bg-[#CCFF00] border-hard rounded-xl px-4 py-2.5 press-btn font-black shadow-hard text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus strokeWidth={3} className="w-4 h-4" />} Gönder
              </button>
            </div>
            {error && <div className="mt-2 border-hard-2 bg-[#FFD0C2] rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="friend-error">{error}</div>}
            {ok && <div className="mt-2 border-hard-2 bg-[#CCFF00] rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="friend-ok">{ok}</div>}
          </form>

          {/* Incoming requests */}
          {incoming.length > 0 && (
            <div className="mb-6" data-testid="incoming-section">
              <div className="text-[11px] uppercase font-black tracking-widest mb-2 px-1">Gelen istekler ({incoming.length})</div>
              <div className="space-y-2">
                {incoming.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 bg-white border-hard-2 rounded-2xl p-3" data-testid={`incoming-req-${r.id}`}>
                    <Avatar src={r.user?.picture} name={r.user?.name} />
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm">{r.user?.name}</div>
                      <div className="text-xs text-[#555] truncate">{r.user?.email}</div>
                    </div>
                    <button onClick={() => accept(r.id)} data-testid={`accept-${r.id}`} className="w-9 h-9 bg-[#CCFF00] border-hard-2 rounded-xl flex items-center justify-center press-btn shadow-hard-sm" aria-label="Kabul et">
                      <Check strokeWidth={3} className="w-4 h-4" />
                    </button>
                    <button onClick={() => decline(r.id)} data-testid={`decline-${r.id}`} className="w-9 h-9 bg-white border-hard-2 rounded-xl flex items-center justify-center press-btn" aria-label="Reddet">
                      <X strokeWidth={3} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outgoing requests */}
          {outgoing.length > 0 && (
            <div className="mb-6" data-testid="outgoing-section">
              <div className="text-[11px] uppercase font-black tracking-widest mb-2 px-1">Bekleyen istekler ({outgoing.length})</div>
              <div className="space-y-2">
                {outgoing.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 bg-white border-hard-2 rounded-2xl p-3 opacity-90" data-testid={`outgoing-req-${r.id}`}>
                    <Avatar src={r.user?.picture} name={r.user?.name} />
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm">{r.user?.name}</div>
                      <div className="text-xs text-[#555] truncate">{r.user?.email} · beklemede</div>
                    </div>
                    <button onClick={() => cancel(r.id)} data-testid={`cancel-${r.id}`} className="text-xs font-black uppercase tracking-widest bg-[#FFF7C2] border-hard-2 rounded-lg px-3 py-1.5 press-btn">İptal</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Friends list */}
          <div>
            <div className="text-[11px] uppercase font-black tracking-widest mb-2 px-1 flex items-center gap-1">
              <Users strokeWidth={3} className="w-3 h-3" /> Arkadaşlarım ({friends.length})
            </div>
            {loading ? (
              <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : friends.length === 0 ? (
              <div className="bg-white border-hard-2 rounded-2xl p-6 text-center" data-testid="friends-empty">
                <div className="font-display text-xl mb-1">Henüz arkadaşın yok</div>
                <div className="text-sm text-[#555]">Yukarıdan e-posta ile yeni arkadaş ekle</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {friends.map((f) => (
                  <Link
                    key={f.id}
                    to={`/app/user/${f.id}`}
                    className="flex items-center gap-3 bg-white border-hard-2 rounded-2xl p-3 press-btn shadow-hard-sm"
                    data-testid={`friend-${f.id}`}
                  >
                    <Avatar src={f.picture} name={f.name} />
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm">{f.name}</div>
                      <div className="text-xs text-[#555] truncate">{f.online ? "🟢 çevrimiçi" : f.email}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
